import { CustomAxiosRequestConfig } from "./index";
import { AxiosDefaults, AxiosHeaderValue, AxiosInstance, AxiosResponse, HeadersDefaults } from "axios";
import { Page } from "@supplier/entity";
import { confirmable } from "@/utils/decorator/confirmable";

export class BaseService<E, V, P, F> {
    protected readonly path!: string;
    protected readonly service: AxiosInstance & {
        get: <T = any, R = AxiosResponse<T>, D = any>(
            url: string,
            data?: D,
            config?: CustomAxiosRequestConfig | Record<string, any>
        ) => Promise<R>;
        post: <T = any, R = AxiosResponse<T>, D = any>(
            url: string,
            data?: D,
            config?: CustomAxiosRequestConfig | Record<string, any>
        ) => Promise<R>;
        put: <T = any, R = AxiosResponse<T>, D = any>(
            url: string,
            data?: D,
            config?: CustomAxiosRequestConfig | Record<string, any>
        ) => Promise<R>;
        delete: <T = any, R = AxiosResponse<T>, D = any>(
            url: string,
            data?: D,
            config?: CustomAxiosRequestConfig | Record<string, any>
        ) => Promise<R>;
    };
    constructor(path: string, service: AxiosInstance, config?: Partial<CustomAxiosRequestConfig>) {
        this.service = service;
        this.path = path;
        this.service.defaults.baseURL = `${this.service.defaults.baseURL}`;
        if (config)
            this.service.defaults = { ...this.service.defaults, ...config } as unknown as Omit<AxiosDefaults, "headers"> & {
                headers: HeadersDefaults & {
                    [key: string]: AxiosHeaderValue;
                };
            };
    }

    /**
     * 主页数据
     */
    public async paging(params: P): Promise<Page<V>> {
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }

    /**
     * 下拉选框列表数据
     */
    public async selector(params: any): Promise<Array<E>> {
        const { data } = await this.service.get(`${this.path}/selector`, { params });
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async get(id: number | string): Promise<E> {
        const { data } = await this.service.get(`${this.path}/${id}`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async getInfo(id: number | string): Promise<V> {
        const { data } = await this.service.get(`${this.path}/vo/${id}`);
        return data;
    }

    /**
     * 创建实例
     * @param form
     */
    @confirmable("是否提交")
    public async create(form: F): Promise<E> {
        for (const key in form) {
            const value = form[key];
            if (value === undefined || value === null || value === "") {
                delete form[key];
            }
        }
        const { data } = await this.service.post(this.path, form, {
            loading: true
        });
        return data;
    }

    /**
     * 更新实例
     * @param id
     * @param form
     */
    @confirmable("是否提交")
    public async update(id: number | string, form: F) {
        const { data } = await this.service.put(`${this.path}/${id}`, form, {
            loading: true
        });
        return data;
    }

    /**
     * 删除实例
     * @param id
     */
    @confirmable("是否删除")
    public async delete(id: number | string) {
        const { data } = await this.service.delete(`${this.path}/${id}`);
        return data;
    }

    /**
     * 下载
     */
    public async download(id: number | string | (number | string)[], name: string = "模板"): Promise<Blob> {
        let path = `${this.path}/download/${id}`;
        if (Array.isArray(id)) {
            path = `${this.path}/download?ids=${id}`;
        }
        const data: Blob = await this.service.get(path, { responseType: "blob" });
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        return data;
    }

    // 预览
    public async preview(id: number | string | (number | string)[]): Promise<void> {
        let path = `${this.path}/download/${id}`;
        if (Array.isArray(id)) {
            path = `${this.path}/download?ids=${id}`;
        }
        const data: Blob = await this.service.get(path, { responseType: "blob" });
        const url = window.URL.createObjectURL(data);
        window.open(url, "_blank");
    }
}
